import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ArrowLeft, Mail, CheckCircle } from 'lucide-react';
import loginBackground from '@/assets/login-background.png';
import { isValidEmail } from '@/utils/emailValidation';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useQuery } from '@tanstack/react-query';
import TurnstileCaptcha, { type TurnstileCaptchaHandle } from '@/components/auth/TurnstileCaptcha';
import useBrandSettings from '@/hooks/useBrandSettings';

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [captchaToken, setCaptchaToken] = useState<string | undefined>();
  const captchaRef = useRef<TurnstileCaptchaHandle>(null);

  const navigate = useNavigate();
  const { toast } = useToast();
  const { data: brandSettings } = useBrandSettings();

  const { data: systemSettings } = useQuery({
    queryKey: ['system-settings-public'],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_system_settings');
      if (error) return null;
      const settings: Record<string, any> = {};
      (data || []).forEach((s: any) => { settings[s.key] = s.value; });
      return settings;
    },
    staleTime: 60000,
  });

  const turnstileEnabled = systemSettings?.turnstile_enabled ?? true;
  const turnstileSiteKey = systemSettings?.turnstile_site_key ?? '0x4AAAAAAB-eeAgUtKF8S5MQ';

  // Verificar se já existe sessão ativa e redirecionar
  useEffect(() => {
    const checkExistingSession = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user) {
        navigate('/inicio');
      }
    };
    checkExistingSession();
  }, [navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!isValidEmail(email)) {
      toast({
        title: 'Email inválido',
        description: 'Por favor, insira um email válido.',
        variant: 'destructive',
      });
      return;
    }
    
    setIsLoading(true);
    
    try {
      const redirectTo = `${window.location.origin}/redefinir-senha`;
      
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo,
        captchaToken: turnstileEnabled ? captchaToken : undefined,
      });
      
      if (error) {
        console.error('Erro ao enviar email:', error);
        toast({
          title: 'Erro ao enviar email',
          description: error.message || 'Não foi possível enviar o email de recuperação.',
          variant: 'destructive',
        });
        return;
      }
      
      setEmailSent(true);
      toast({
        title: 'Email enviado',
        description: 'Verifique sua caixa de entrada para redefinir sua senha.',
      });
      
    } catch (error: any) {
      console.error('Erro:', error);
      toast({
        title: 'Erro',
        description: 'Ocorreu um erro inesperado. Tente novamente.',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
      // Token Turnstile é de uso único — sempre resetar após o submit
      setCaptchaToken(undefined);
      captchaRef.current?.reset();
    }
  };

  return (
    <div 
      className="flex flex-col min-h-screen p-4 relative bg-cover bg-center bg-no-repeat"
      style={{ backgroundImage: `url(${loginBackground})` }}
    >
      {/* Glassmorphism overlay */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm"></div>

      {/* Branding */}
      <div className="flex-1 flex items-end justify-center pb-8 relative z-10 animate-fade-in">
        <div className="text-center">
          <div className="flex items-center justify-center gap-4 mb-4">
            <img 
              src={brandSettings?.symbolLight || brandSettings?.symbolDark || 'https://mdckcgxuuyspxxnxuiuc.supabase.co/storage/v1/object/public/brand-assets/brand/symbol_dark_1764955794247.png'} 
              alt={brandSettings?.platformName || 'AutoFluxo'} 
              className="h-16 drop-shadow-2xl"
            />
            <img 
              src="https://mdckcgxuuyspxxnxuiuc.supabase.co/storage/v1/object/public/brand-assets/brand/logo_text_dark_1764955810339.png"
              alt={brandSettings?.platformName || 'AutoFluxo'} 
              className="h-12 drop-shadow-lg"
            />
          </div>
          <p className="text-white/80 drop-shadow-md">
            {brandSettings?.platformTagline || 'O sistema que faz sua revenda crescer'}
          </p>
        </div>
      </div>

      {/* Form */}
      <div className="flex-1 flex items-start justify-center pt-8">
        <div className="w-full max-w-md relative z-10 animate-fade-in">
          <Card className="border border-white/10 bg-black/30 backdrop-blur-xl shadow-2xl rounded-2xl">
            <CardHeader className="text-center">
              <CardTitle className="text-white">
                {emailSent ? 'Email Enviado!' : 'Recuperar Senha'}
              </CardTitle>
              <CardDescription>
                {emailSent 
                  ? 'Verifique sua caixa de entrada'
                  : 'Informe seu email para receber o link de recuperação'
                }
              </CardDescription>
            </CardHeader>
            <CardContent>
              {emailSent ? (
                <div className="text-center space-y-6">
                  <div className="flex justify-center">
                    <div className="p-4 bg-green-500/20 rounded-full">
                      <CheckCircle className="h-12 w-12 text-green-500" />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <p className="text-white/80 text-sm">
                      Enviamos um link de recuperação para:
                    </p>
                    <p className="text-white font-medium">{email}</p>
                  </div>
                  <p className="text-white/60 text-xs">
                    O link expira em 1 hora. Se não receber, verifique sua pasta de spam.
                  </p>
                  <Button
                    variant="outline"
                    onClick={() => setEmailSent(false)}
                    className="w-full bg-white/10 border-white/20 text-white hover:bg-white/20"
                  >
                    Enviar novamente
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Input
                      type="email"
                      placeholder="Digite seu email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="bg-black/30 border-white/20 text-white placeholder:text-white/70 backdrop-blur-sm focus:bg-black/40 focus:border-white/40"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <TurnstileCaptcha
                      ref={captchaRef}
                      enabled={turnstileEnabled}
                      siteKey={turnstileSiteKey}
                      onSuccess={(token) => setCaptchaToken(token)}
                      onExpire={() => setCaptchaToken(undefined)}
                      onError={() => setCaptchaToken(undefined)}
                    />
                  </div>
                  <Button 
                    type="submit" 
                    className="w-full bg-imperial-red hover:bg-red-700"
                    disabled={isLoading || (turnstileEnabled && !captchaToken)}
                  >
                    {isLoading ? (
                      <span className="flex items-center">
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Enviando...
                      </span>
                    ) : (
                      <>
                        <Mail className="mr-2 h-4 w-4" />
                        Enviar Link de Recuperação
                      </>
                    )}
                  </Button>
                </form>
              )}
            </CardContent>
          </Card>

          {/* Back to login */}
          <div className="mt-6 text-center">
            <Link 
              to="/login" 
              className="text-sm text-white/70 hover:text-white transition-colors inline-flex items-center gap-2"
            >
              <ArrowLeft className="h-4 w-4" />
              Voltar para o login
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
